import { createReducer, on, Action } from '@ngrx/store';
import * as fromAppstateActions from './appstate.actions';
import {
  AlertInfo,
  BrNavigationResponse,
  Clients,
  NavigationResponse,
  GetWelcomeTileResponse,
  ToolsResponse,
  UserAgreementInfo,
  InstantALertData,
} from './appstate.models';
import { ServiceError } from '@cigna/cigna-for-brokers/shared/util';
import * as validatorReducerUtil from '../util/validate-reducer.util';
export const APPSTATE_FEATURE_KEY = 'appstate';

export interface AppState {
  Onboarding: any;
  alert?: AlertInfo;
  userAgreement?: UserAgreementInfo;
  navigation?: NavigationResponse;
  brNavigation?: BrNavigationResponse;
  welcome?: GetWelcomeTileResponse;
  businessAlert?: InstantALertData;
  detailsLoaded: boolean;
  detailsError?: ServiceError;
  additionalDetailCode?: string;
  resizeSideNavToggle?: boolean;
  appRedirect?: string | undefined;
  isInternalUser?: boolean;
  isBroker?: boolean;
  legalAffirmStatus: boolean;
  statementAccessRequest: boolean;
  tools: ToolsResponse;
  lobBeforeLogin?: string | undefined;
  lastVisitedUser?: string | undefined;
}
export const initialState: AppState = {
  alert: undefined,
  userAgreement: undefined,
  navigation: undefined,
  resizeSideNavToggle: false,
  detailsLoaded: true,
  additionalDetailCode: '0',
  appRedirect: undefined,
  legalAffirmStatus: false,
  statementAccessRequest: false,
  tools: { contentType: '', contents: [] },
  lobBeforeLogin: undefined,
  Onboarding: undefined,
};

export interface AppStatePartialState {
  readonly [APPSTATE_FEATURE_KEY]: AppState;
}
const appstateReducer = createReducer(
  initialState,
  on(fromAppstateActions.updateUserType, (state, { flag }) => ({
    ...state,
    isInternalUser: flag,
  })),
  on(fromAppstateActions.getAlerts, (state, _action) => ({
    ...state,
    additionalDetailCode: '0',
    detailsLoaded: false,
    detailsError: undefined,
  })),
  on(fromAppstateActions.getAlertsSuccess, (state, { payload }) => ({
    ...state,
    alert: payload.data,
    additionalDetailCode: validatorReducerUtil.extractAdditionalDetails(
      payload.metadata,
    ),
    detailsLoaded: true,
    detailsError: undefined,
  })),
  on(fromAppstateActions.getAlertsFailure, (state, { error }) => ({
    ...state,
    additionalDetailCode: '0',
    detailsLoaded: true,
    detailsError: error,
  })),
  /* eslint-disable sonarjs/no-identical-functions */
  on(fromAppstateActions.dismissAlert, (state, _action) => ({
    ...state,
    detailsLoaded: false,
    additionalDetailCode: '0',
    detailsError: undefined,
  })),
  on(
    fromAppstateActions.dismissAlertSuccess,
    (state, { payload, alertMsgId }) => ({
      ...state,
      alert: state.alert
        ? {
            contentType: state.alert.contentType,
            contents: state.alert.contents.map((content) => {
              if (alertMsgId.includes(content.content.alertMsgId)) {
                return { alertSeen: true, content: content.content };
              }
              return content;
            }),
          }
        : undefined,
      additionalDetailCode: validatorReducerUtil.extractAdditionalDetails(
        payload.metadata,
      ),
      detailsLoaded: true,
      detailsError: undefined,
    }),
  ),
  /* eslint-disable sonarjs/no-identical-functions */
  on(fromAppstateActions.dismissAlertFailure, (state, { error }) => ({
    ...state,
    searchedClients: undefined,
    detailsLoaded: true,
    additionalDetailCode: '0',
    detailsError: error,
  })),
  on(fromAppstateActions.getUserAgreement, (state, _action) => ({
    ...state,
    additionalDetailCode: '0',
    detailsLoaded: false,
    detailsError: undefined,
  })),
  on(fromAppstateActions.getUserAgreementSuccess, (state, { payload }) => ({
    ...state,
    userAgreement: payload.data,
    additionalDetailCode: validatorReducerUtil.extractAdditionalDetails(
      payload.metadata,
    ),
    detailsLoaded: true,
    detailsError: undefined,
  })),
  on(fromAppstateActions.getUserAgreementFailure, (state, { error }) => ({
    ...state,
    additionalDetailCode: '0',
    detailsLoaded: true,
    detailsError: error,
  })),
  on(fromAppstateActions.updateTermsAgreementStatus, (state, _action) => ({
    ...state,
    additionalDetailCode: '0',
    detailsLoaded: false,
    detailsError: undefined,
  })),
  on(
    fromAppstateActions.updateTermsAgreementStatusSuccess,
    (state, { payload }) => ({
      ...state,
      userAgreementUpdated: payload.data,
      additionalDetailCode: validatorReducerUtil.extractAdditionalDetails(
        payload.metadata,
      ),
      detailsLoaded: true,
      detailsError: undefined,
    }),
  ),
  on(
    fromAppstateActions.updateTermsAgreementStatusFailure,
    (state, { error }) => ({
      ...state,
      additionalDetailCode: '0',
      detailsLoaded: true,
      detailsError: error,
    }),
  ),
  on(fromAppstateActions.getBrokerNavigations, (state, _action) => ({
    ...state,
    detailsLoaded: false,
    additionalDetailCode: '0',
    detailsError: undefined,
  })),
  on(fromAppstateActions.getBrokerNavigationsSuccess, (state, { payload }) => ({
    ...state,
    brNavigation: payload.data,
    isBroker: true,
    additionalDetailCode: validatorReducerUtil.extractAdditionalDetails(
      payload.metadata,
    ),
    detailsLoaded: true,
    detailsError: undefined,
  })),
  on(fromAppstateActions.getWelcomeTileSuccess, (state, { payload }) => ({
    ...state,
    welcome: payload,
    detailsLoaded: true,
    detailsError: undefined,
  })),
  on(fromAppstateActions.getBusinessAlertsSuccess, (state, { payload }) => ({
    ...state,
    businessAlert: payload,
    detailsLoaded: true,
    detailsError: undefined,
  })),
  /* eslint-disable sonarjs/no-identical-functions */
  on(fromAppstateActions.searchClient, (state, _action) => ({
    ...state,
    detailsLoaded: false,
    additionalDetailCode: '0',
    detailsError: undefined,
  })),
  on(fromAppstateActions.searchClientSuccess, (state, { payload }) => ({
    ...state,
    searchedClients: payload,
    additionalDetailCode: validatorReducerUtil.extractAdditionalDetails(
      payload.metadata,
    ),
    detailsLoaded: true,
    detailsError: undefined,
  })),
  /* eslint-disable sonarjs/no-identical-functions */
  on(fromAppstateActions.searchClientFailure, (state, { error }) => ({
    ...state,
    searchedClients: undefined,
    detailsLoaded: true,
    additionalDetailCode: '0',
    detailsError: error,
  })),
  on(fromAppstateActions.checkIpValid, (state, _action) => ({
    ...state,
    detailsLoaded: false,
    additionalDetailCode: '0',
    detailsError: undefined,
  })),
  on(fromAppstateActions.checkIpValidSuccess, (state, { payload }) => ({
    ...state,

    additionalDetailCode: validatorReducerUtil.extractAdditionalDetails(
      payload.metadata,
    ),
    detailsLoaded: true,
    detailsError: undefined,
  })),
  /* eslint-disable sonarjs/no-identical-functions */
  on(fromAppstateActions.checkIpValidFailure, (state, { error }) => ({
    ...state,

    detailsLoaded: true,
    additionalDetailCode: '0',
    detailsError: error,
  })),
  on(fromAppstateActions.resizeSideNavToggle, (state, data) => ({
    ...state,
    resizeSideNavToggle: data.open,
  })),
  on(fromAppstateActions.appRedirect, (state, data) => ({
    ...state,
    appRedirect: data.redirect,
  })),
  on(fromAppstateActions.legalAffirmStatus, (state, data) => ({
    ...state,
    legalAffirmStatus: data.open,
  })),
  on(fromAppstateActions.getTools, (state) => ({
    ...state,
    tools: { contentType: '', contents: [] },
  })),
  on(fromAppstateActions.getToolsSuccess, (state, { payload }) => ({
    ...state,
    tools: payload,
  })),
  on(fromAppstateActions.getToolsFailure, (state, { error }) => ({
    ...state,
    tools: { contentType: ',', contents: [] },
    detailsError: error,
  })),
  on(fromAppstateActions.statementAccessRequest, (state, data) => ({
    ...state,
    statementAccessRequest: data.isEnabled,
  })),
  on(fromAppstateActions.setLob, (state, data) => ({
    ...state,
    lobBeforeLogin: data.lob,
  })),
  on(fromAppstateActions.setLastVisitedUser, (state, data) => ({
    ...state,
    lastVisitedUser: data.lastVisitedUser,
  })),
  on(fromAppstateActions.addBasicInfo, (state, payload) => ({
    ...state,
    Onboarding: {
      ...state.Onboarding,
      basicInfo: {
        firstName: payload.firstName,
        lastName: payload.lastName,
        emailAddress: payload.emailAddress,
        mobileNumber: payload.mobileNumber,
        npn: payload.npn,
        agencyName: payload.agencyName,
      },
    },
  })),
  on(fromAppstateActions.addAddress, (state, payload) => ({
    ...state,
    Onboarding: {
      ...state.Onboarding,
      addressInfo: {
        physicalAddress: {
          street1: payload.physicalAddress.street1,
          street2: payload.physicalAddress.street2,
          city: payload.physicalAddress.city,
          state: payload.physicalAddress.state,
          zipCode: payload.physicalAddress.zipCode,
        },
        mailingAddress: {
          street1: payload.mailingAddress.street1,
          street2: payload.mailingAddress.street2,
          city: payload.mailingAddress.city,
          state: payload.mailingAddress.state,
          zipCode: payload.mailingAddress.zipCode,
        },
        isSameAsPhysical: payload.isSameAsPhysical,
      },
    },
  })),
  on(fromAppstateActions.addCommercialFlag, (state, payload) => ({
    ...state,
    Onboarding: {
      ...state.Onboarding,
      isCommercialOpted: payload.isCommercialOpted,
    },
  })),
  on(fromAppstateActions.addIfpFlag, (state, payload) => ({
    ...state,
    Onboarding: {
      ...state.Onboarding,
      isIfpOpted: payload.isIfpOpted,
    },
  })),
  on(fromAppstateActions.addMaFlag, (state, payload) => ({
    ...state,
    Onboarding: {
      ...state.Onboarding,
      isMaOpted: payload.isMaOpted,
    },
  })),
  on(fromAppstateActions.addContinueCommercial, (state, payload) => ({
    ...state,
    Onboarding: {
      ...state.Onboarding,
      isContinueCommercial: payload.isContinueCommercial,
    },
  })),
  on(fromAppstateActions.addContinueIfp, (state, payload) => ({
    ...state,
    Onboarding: {
      ...state.Onboarding,
      isContinueIfp: payload.isContinueIfp,
    },
  })),
  on(fromAppstateActions.addContinueMa, (state, payload) => ({
    ...state,
    Onboarding: {
      ...state.Onboarding,
      isContinueMa: payload.isContinueMa,
    },
  })),
  on(fromAppstateActions.addCommercialInfo, (state, payload) => ({
    ...state,
    Onboarding: {
      ...state.Onboarding,
      commercial: {
        isExistingOpportunity: payload.isExistingOpportunity,
        employerName: payload.employerName,
        noOfEmployee: payload.noOfEmployee,
        workState: payload.workState,
      },
    },
  })),
  on(fromAppstateActions.addIfpInfo, (state, payload) => ({
    ...state,
    Onboarding: {
      ...state.Onboarding,
      ifp: {
        isLicensedCt: payload.isLicensedCt,
        producerstate: payload.producerstate,
        statesLicensed: payload.statesLicensed,
      },
    },
  })),
  on(fromAppstateActions.addMaInfo, (state, payload) => ({
    ...state,
    Onboarding: {
      ...state.Onboarding,
      ma: {
        isLicensedCt: payload.isLicensedCt,
        producerstate: payload.producerstate,
        statesLicensed: payload.statesLicensed,
      },
    },
  })),
  on(fromAppstateActions.deleteOnboardingInfo, (state, payload) => ({
    ...state,
    Onboarding: undefined,
  })),
);

export function reducer(state: AppState | undefined, action: Action) {
  return appstateReducer(state, action);
}

export function setSelectedClientDetails(
  clients: Clients[] | undefined,
  defaultClientid: number | undefined,
): Clients | undefined {
  let clientInd = 0;
  if (defaultClientid && clients) {
    clientInd = clients.findIndex((client) => client.cs1id === defaultClientid);
    clientInd = clientInd > -1 ? clientInd : 0;
  }
  const selectedClient = clients ? clients[clientInd] : undefined;

  if (selectedClient) {
    localStorage.setItem('group-id', selectedClient.cs1id.toString());
    localStorage.setItem('client-id', selectedClient.id.toString());
    localStorage.setItem('source-cd', selectedClient.sourceCode.toString());
    localStorage.setItem('portal', 'cfe');
  }
  return selectedClient;
}
